import { ChainedCommands, Editor } from "@tiptap/react";
import { getSelectedAnchorElement } from "../../../../utils/LinkUtils";

// Function to maintain text alignment when toggling headings or paragraph
export function editorChainKeepAlign(editor: Editor, chain: ChainedCommands) {
  if (editor.isActive({ textAlign: "left" })) chain.setTextAlign("left");
  else if (editor.isActive({ textAlign: "right" })) chain.setTextAlign("right");
  else if (editor.isActive({ textAlign: "center" }))
    chain.setTextAlign("center");
}

// Helper function to handle style toggling logic for various formatting options
export function handleStyleToggle(
  type: "bold" | "italic" | "underline" | "strike",
  styleProperty: string,
  styleValue: string,
  nodeName: string,
  unsetCommand: (chain: ChainedCommands) => ChainedCommands,
  toggleCommand: (chain: ChainedCommands) => ChainedCommands,
  editor: Editor
) {
  const selectedAnchor: HTMLAnchorElement | null = getSelectedAnchorElement();
  if (selectedAnchor) {
    const activeByStyle = selectedAnchor.style[styleProperty] === styleValue;

    let activeByDecoration: any = selectedAnchor.parentNode;
    let isActiveByDecoration = activeByDecoration.nodeName === nodeName;

    if (!isActiveByDecoration) {
      activeByDecoration = activeByDecoration.parentNode;
      isActiveByDecoration = activeByDecoration.nodeName === nodeName;
    }

    if (activeByStyle && isActiveByDecoration) {
      selectedAnchor.style[styleProperty] = "none !important";
      unsetCommand(editor.chain()).run();
    } else if (isActiveByDecoration || activeByStyle) {
      unsetCommand(editor.chain()).run();
    } else {
      toggleCommand(editor.chain()).run();
    }
  } else {
    toggleCommand(editor.chain()).run();
  }
  return true;
}

export function handleBold(editor: Editor) {
  return handleStyleToggle(
    "bold",
    "fontWeight",
    "bold",
    "STRONG",
    (chain) => chain.unsetBold(),
    (chain) => chain.toggleBold(),
    editor
  );
}

export function handleItalic(editor: Editor) {
  return handleStyleToggle(
    "italic",
    "fontStyle",
    "italic",
    "EM",
    (chain) => chain.unsetItalic(),
    (chain) => chain.toggleItalic(),
    editor
  );
}

export function handleUnderline(editor: Editor) {
  return handleStyleToggle(
    "underline",
    "textDecoration",
    "underline",
    "U",
    (chain) => chain.unsetUnderline(),
    (chain) => chain.toggleUnderline(),
    editor
  );
}

export function handleStrikethrough(editor: Editor) {
  return handleStyleToggle(
    "strike",
    "textDecoration",
    "line-through",
    "S",
    (chain) => chain.unsetStrike(),
    (chain) => chain.toggleStrike(),
    editor
  );
}

export function handleHeading(level: any, editor: Editor) {
  let chain = editor.chain().toggleHeading({ level });
  editorChainKeepAlign(editor, chain);
  chain.run();
}

export function handleH1(editor: Editor) {
  handleHeading(1, editor);
}

export function handleH2(editor: Editor) {
  handleHeading(2, editor);
}

export function handleH3(editor: Editor) {
  handleHeading(3, editor);
}

export function handleParagraph(editor: Editor) {
  let chain = editor.chain().setParagraph();
  editorChainKeepAlign(editor, chain);
  chain.run();
}

export function isElementActive(
  type: "bold" | "italic" | "underline" | "strike",
  editor: Editor
): boolean {
  const selectedAnchor: HTMLAnchorElement | null = getSelectedAnchorElement();

  if (!selectedAnchor) {
    return editor.isActive(type);
  }

  let typeByStyle: boolean;
  let isTypeByDecoration: boolean;
  let typeByDecoration = selectedAnchor.parentNode;
  let nodeName: string;

  switch (type) {
    case "bold":
      typeByStyle = /^(bold(er)?|[5-9]\d{2,})$/.test(
        selectedAnchor.style.fontWeight as string
      );
      nodeName = "STRONG";
      break;
    case "italic":
      typeByStyle = selectedAnchor.style.fontStyle === type;
      nodeName = "EM";
      break;
    case "underline":
    case "strike":
      typeByStyle = selectedAnchor.style.textDecoration === type;
      nodeName = type === "underline" ? "U" : "S";
      break;
    default:
      return false;
  }

  isTypeByDecoration = typeByDecoration.nodeName === nodeName;

  return typeByStyle || isTypeByDecoration;
}

export function handleMouseDown(
  e: React.MouseEvent,
  callback: () => void,
  preventDefault = true,
  stopPropagation = true
) {
  if (preventDefault) {
    e.preventDefault();
  }
  if (stopPropagation) {
    e.stopPropagation();
  }
  callback();
}
